import { Application, Graphics } from "pixi.js";
import { Obstacle } from "./Obstacle";
import {
  randomYPos,
  randomXPos,
  circleColors,
  topLimit,
  bottomLimit,
  circleRadius,
  gameWidth,
  backgroundColor,
  obstacleSpeedModifier,
  baseObstacleSpeed,
  lineColor,
  lineHeight,
  Difficulty,
  easyColorCount,
  easyObstacleRadius,
} from "../config";
import Player from "./Player";
import PowerUpMeter from "./PowerUpMeter";
import CollectionParticle from "./CollectionParticle";
import TrailParticle from "./TrailParticle";
import BurstParticle from "./BurstParticle";
import score from "../score";
import Circle from "./Circle";

export class GameApp {
  app: Application;
  scoreElement: HTMLElement;
  obstacles: Obstacle[];
  obstaclesCreated: number;
  player: Player;
  score: number;
  powerUpMeter: PowerUpMeter;
  replay: () => void;
  difficulty: Difficulty;
  private availableColors: number[];
  private obstacleRadius: number;

  constructor(
    scoreElement: HTMLElement,
    replay: () => void,
    difficulty: Difficulty = "hard",
  ) {
    this.replay = replay;
    this.difficulty = difficulty;
    this.app = new Application();

    this.obstacles = [];
    this.obstaclesCreated = 0;

    const colors = circleColors();
    this.availableColors =
      difficulty === "easy" ? colors.slice(0, easyColorCount) : colors;
    this.obstacleRadius =
      difficulty === "easy" ? easyObstacleRadius : circleRadius;

    this.score = 0;
    this.scoreElement = scoreElement;
    this.scoreElement.innerText = `SCORE: ${this.score}`;
  }

  private randomColor = () => {
    return this.availableColors[
      Math.floor(Math.random() * this.availableColors.length)
    ];
  };

  private randomPlayerColor = () => {
    if (this.obstacles.length > 0 && this.obstaclesCreated % 3 === 0) {
      return this.obstacles[Math.floor(Math.random() * this.obstacles.length)]
        .color;
    }
    return this.randomColor();
  };

  async init(parent: HTMLElement, width: number, height: number) {
    await this.app.init({
      width,
      height,
      backgroundColor: backgroundColor(),
      antialias: true,
    });
    parent.append(this.app.canvas);
  }

  beginGame = () => {
    this.addPowerUpMeter();
    this.addLines();

    this.addObstacle();
    this.addPlayer();
    this.app.ticker.add((ticker) => this.update(ticker.deltaTime));
  };

  private addObstacle = () => {
    const newCircle = new Obstacle({
      speed: baseObstacleSpeed + this.obstaclesCreated / obstacleSpeedModifier,
      y: randomYPos(),
      x: randomXPos(),
      color: this.randomColor(),
      radius: this.obstacleRadius,
    });
    this.obstacles.push(newCircle);
    this.obstaclesCreated++;
    this.app.stage.addChild(newCircle);
  };

  private addLine = (y: number) => {
    const line = new Graphics();
    line.rect(0, y, gameWidth, lineHeight).fill(lineColor());
    this.app.stage.addChild(line);
  };

  private addLines = () => {
    this.addLine(topLimit - circleRadius);
    this.addLine(bottomLimit + circleRadius);
  };

  private increaseScore = () => {
    this.score++;
    this.scoreElement.innerText = `SCORE: ${this.score}`;
  };

  private addPlayer = () => {
    const player = new Player({
      speed: 0,
      color: this.randomPlayerColor(),
      increaseScore: this.increaseScore,
      crossedCallback: this.addObstacle,
      newColor: this.randomPlayerColor,
    });
    this.player = player;
    this.app.stage.addChild(this.player);
  };

  private spawnParticle = (obstacle: Obstacle) => {
    const particle = new CollectionParticle({
      x: obstacle.x,
      y: obstacle.y,
      color: obstacle.color,
    });
    this.app.stage.addChild(particle);
    return particle
      .moveToPowerUpMeter(this.powerUpMeter.nextPosition())
      .then(() => particle.destroy());
  };

  private collectObstacle = (obstacle: Obstacle) => {
    obstacle.speed = 0;
    obstacle.collected = true;
    this.spawnBurst(obstacle.x, obstacle.y, obstacle.color);
    if (!this.player.poweredUp && !this.powerUpMeter.full) {
      this.spawnParticle(obstacle).then(() =>
        this.powerUpMeter.collect(obstacle.color),
      );
    }
    obstacle
      .disappear()
      .then(
        () => (this.obstacles = this.obstacles.filter((o) => o !== obstacle)),
      );
  };

  private showEndGameUI = (score: number, newHighScore: boolean) => {
    document.querySelector(".buttons>.message>span").innerHTML =
      score.toString();
    const message = newHighScore ? "New high score!" : "You scored";
    document.querySelector(".buttons>.message>div").innerHTML = message;
    document.getElementById("endgame").style.display = "block";
    document
      .getElementById("play-again")
      .addEventListener("click", this.replay);
  };

  private gameOver = () => {
    if (this.player.dead) return;
    this.player.die();
    this.showEndGameUI(
      this.score,
      score.checkHighScore(this.score, this.difficulty),
    );
  };

  private obstacleCollisionCheck = () => {
    this.obstacles
      .filter((o) => !o.collected)
      .forEach((obstacle) => {
        const distance = Math.hypot(
          obstacle.x - this.player.x,
          obstacle.y - this.player.y,
        );
        const beNice =
          this.player.poweredUp || obstacle.color === this.player.color;
        const niceBuffer = beNice ? 6 : -6;
        if (distance < this.player.radius + obstacle.radius + niceBuffer) {
          if (beNice) this.collectObstacle(obstacle);
          else this.gameOver();
        }
      });
  };

  private addPowerUpMeter = () => {
    this.powerUpMeter = new PowerUpMeter(this.powerUpEnabled);
    this.app.stage.addChild(this.powerUpMeter);
  };

  private powerUpEnabled = () => {
    this.player.enablePowerUp(this.powerUpMeter.reset);
  };

  private onAssetsLoaded = () => {};

  private update = (delta: number) => {
    this.obstacles.forEach((c) => {
      c.update(delta);
      this.addTrailParticle(c, 0.85);
    });
    this.player.update(delta);
    this.obstacleCollisionCheck();

    if (this.player.launched && !this.player.dead) {
      this.addTrailParticle(this.player, 0.75);
    }
  };

  private spawnBurst = (x: number, y: number, color: number) => {
    const count = 8;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const particle = new BurstParticle({ x, y, color, angle });
      this.app.stage.addChild(particle);
    }
  };

  private addTrailParticle = (circle: Circle, duration?: number) => {
    const particle = new TrailParticle({
      x: circle.x,
      y: circle.y,
      color: circle.color,
      radius: circleRadius * 0.3,
      duration,
      moveABit: circle === this.player, // only move the particle if it's from the player for a cooler effect
    });
    this.app.stage.addChildAt(particle, 0); // Add behind other objects
  };
}
